import { useSearchParams } from 'react-router-dom';
import { Select } from '../components/ui/Select';
import { ComplaintDetailModal } from '../components/complaints/ComplaintDetailModal';
import { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  RotateCcw,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { complaintsApi, type AdminComplaint } from '../lib/api';
import { EmptyState } from '../components/EmptyState';

// ============================================================================
// Helpers
// ============================================================================

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  RETURN: { label: 'Zwrot', color: 'text-accent-info', bg: 'bg-info/20' },
  COMPLAINT: { label: 'Reklamacja', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  EXCHANGE: { label: 'Wymiana', color: 'text-purple-400', bg: 'bg-purple-500/20' },
};

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  SUBMITTED: { label: 'Nowe', color: 'text-accent-info', bg: 'bg-info/20' },
  IN_REVIEW: { label: 'W rozpatrywaniu', color: 'text-warning', bg: 'bg-warning/20' },
  RESOLVED: { label: 'Zakończone', color: 'text-success', bg: 'bg-success/20' },
  ACCEPTED: { label: 'Zaakceptowane', color: 'text-success', bg: 'bg-success/20' },
  REJECTED: { label: 'Odrzucone', color: 'text-danger', bg: 'bg-danger/20' },
};

const STATUS_OPTIONS = [
  { value: 'SUBMITTED', label: 'Nowe' },
  { value: 'IN_REVIEW', label: 'W rozpatrywaniu' },
  { value: 'RESOLVED', label: 'Zakończone' },
  { value: 'ACCEPTED', label: 'Zaakceptowane' },
  { value: 'REJECTED', label: 'Odrzucone' },
];

// ============================================================================
// ComplaintsPage
// ============================================================================

export function ComplaintsPage() {
  const [searchParams] = useSearchParams();
  // A link to a status starts at page one, including navigation from the bell.
  return <ComplaintsContent key={searchParams.get('status') || ''} />;
}

function ComplaintsContent() {
  const { showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedComplaintId = searchParams.get('complaintId');
  const requestedStatus = searchParams.get('status') || '';
  const statusFilter = STATUS_OPTIONS.some(option => option.value === requestedStatus) ? requestedStatus : '';
  const [complaints, setComplaints] = useState<AdminComplaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ type: '', search: '' });
  const [searchDebounce, setSearchDebounce] = useState('');
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchDebounce }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchDebounce]);

  const loadComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter) params.status = statusFilter;
      if (filters.type) params.type = filters.type;
      if (filters.search) params.search = filters.search;

      const { data } = await complaintsApi.getAll(params);
      setComplaints(data.complaints);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages,
      }));
    } catch {
      showError('Nie udało się pobrać zgłoszeń');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, filters.type, filters.search, showError]);

  useEffect(() => { loadComplaints(); }, [loadComplaints]);

  const openDetail = (complaint: AdminComplaint) => setSearchParams(previous => {
    const next = new URLSearchParams(previous);
    next.set('complaintId', complaint.id);
    return next;
  });

  const stats = {
    total: pagination.total,
    pending: complaints.filter(c => c.status === 'SUBMITTED').length,
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Zwroty i reklamacje</h1>
            <p className="text-text-secondary mt-2">
              Zarządzaj zgłoszeniami klientów
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span className="px-2 py-1 rounded-lg bg-surface-hover">
                Łącznie: <strong className="text-text-primary">{stats.total}</strong>
              </span>
              {stats.pending > 0 && (
                <span className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-400">
                  Oczekujące: <strong>{stats.pending}</strong>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 animate-slide-up">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchDebounce}
              onChange={e => setSearchDebounce(e.target.value)}
              placeholder="Szukaj po numerze zamówienia, kliencie..."
              className="input pl-10 w-full"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={e => { setSearchParams(previous => { const next = new URLSearchParams(previous); if (e.target.value) next.set('status', e.target.value); else next.delete('status'); return next; }); setPagination(p => ({ ...p, page: 1 })); }}
            className="input w-auto min-w-[160px]"
          >
            <option value="">Wszystkie statusy</option>
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <Select
            value={filters.type}
            onChange={e => { setFilters(f => ({ ...f, type: e.target.value })); setPagination(p => ({ ...p, page: 1 })); }}
            className="input w-auto min-w-[140px]"
          >
            <option value="">Wszystkie typy</option>
            <option value="RETURN">Zwrot</option>
            <option value="COMPLAINT">Reklamacja</option>
            <option value="EXCHANGE">Wymiana</option>
          </Select>
        </div>

        {/* Table */}
        <div className="card overflow-hidden animate-slide-up">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-text-secondary" />
            </div>
          ) : complaints.length === 0 ? (
            <EmptyState
              icon={RotateCcw}
              title="Brak zgłoszeń"
              description="Zgłoszenia od klientów pojawią się tutaj"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Zamówienie</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Klient</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Typ</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Data</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider">Sklep</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-text-secondary uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-700">
                  {complaints.map(c => {
                    const typeInfo = TYPE_LABELS[c.type] || { label: c.type, color: 'text-text-secondary', bg: 'bg-gray-500/20' };
                    const statusInfo = STATUS_LABELS[c.status] || { label: c.status, color: 'text-text-secondary', bg: 'bg-gray-500/20' };
                    return (
                      <tr
                        key={c.id}
                        onClick={() => openDetail(c)}
                        className="hover:bg-surface-hover/50 cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-text-primary">#{c.order.orderNumber}</span>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-text-primary">{c.customerName || '—'}</p>
                          <p className="text-xs text-text-muted">{c.customerEmail}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color} ${typeInfo.bg}`}>
                            {typeInfo.label}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-text-secondary">
                            {new Date(c.createdAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color} ${statusInfo.bg}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-text-secondary">{c.order.store.name}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Eye className="w-4 h-4 text-text-muted inline-block" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-text-secondary">
                Strona {pagination.page} z {pagination.pages} ({pagination.total} zgłoszeń)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                  disabled={pagination.page >= pagination.pages}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {linkedComplaintId && <ComplaintDetailModal complaintId={linkedComplaintId} onClose={() => { setSearchParams(previous => { const next = new URLSearchParams(previous); next.delete('complaintId'); return next; }, { replace: true }); }} onUpdated={() => void loadComplaints()} />}
    </>
  );
}
